export const DEFAULT_RADIUS_KM = 10;
export const MAX_RADIUS_KM = 100;
export const MIN_RADIUS_KM = 1;
export const RADIUS_SLIDER_BASE_MAX_KM = 50;
export const RADIUS_STEP_KM = 1;

export const MIN_VOLUME_KG = 0;
export const MAX_VOLUME_KG = 250;
export const VOLUME_STEP_KG = 5;

export const MIN_PRICE_PER_KG = 0;
export const MAX_PRICE_PER_KG = 50;
export const PRICE_STEP_PER_KG = 0.5;

export const MAX_DISTANCE_METERS = 100000;
export const COORDINATE_PRECISION = 3;
export const FEED_PAGE_SIZE = 50;
export const FEED_SORT = "distance,asc";

export const INITIAL_FILTERS = Object.freeze({
  containerOptionId: null,
  containerTypeId: null,
  fuelTypeId: null,
  maxVolume: MAX_VOLUME_KG,
  maxPricePerKg: MAX_PRICE_PER_KG,
  radiusKm: DEFAULT_RADIUS_KM,
});

export const PRODUCT_FILTER_KEYS = Object.freeze([
  "containerOptionId",
  "containerTypeId",
  "fuelTypeId",
  "maxVolume",
  "maxPricePerKg",
]);

export const FILTER_KEYS = Object.freeze([...PRODUCT_FILTER_KEYS, "radiusKm"]);

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : null;
};

const clampRange = (value, min, max) => Math.min(Math.max(value, min), max);

export const quantise = (value, precision = COORDINATE_PRECISION) => {
  const parsed = toNumber(value);
  if (parsed === null) return null;

  const factor = 10 ** precision;

  return Math.round(parsed * factor) / factor;
};

export const quantiseCoordinates = (coordinates) => {
  if (!coordinates) return null;

  const latitude = quantise(coordinates.latitude);
  const longitude = quantise(coordinates.longitude);

  if (latitude === null || longitude === null) return null;

  return { latitude, longitude };
};

export const sameSearchCell = (left, right) => {
  const a = quantiseCoordinates(left);
  const b = quantiseCoordinates(right);

  if (!a || !b) return a === b;

  return a.latitude === b.latitude && a.longitude === b.longitude;
};

export const clampRadiusKm = (radiusKm) => {
  const parsed = toNumber(radiusKm);
  if (parsed === null) return DEFAULT_RADIUS_KM;

  return Math.min(Math.max(parsed, MIN_RADIUS_KM), MAX_RADIUS_KM);
};

export const radiusSliderMaxKm = (radiusKm) => {
  const current = Math.ceil(clampRadiusKm(radiusKm));

  return Math.min(Math.max(RADIUS_SLIDER_BASE_MAX_KM, current), MAX_RADIUS_KM);
};

export const radiusToMeters = (radiusKm) =>
  Math.min(Math.round(clampRadiusKm(radiusKm) * 1000), MAX_DISTANCE_METERS);

export const normaliseFilters = (filters) => {
  const source = filters || {};

  return {
    containerOptionId: toNumber(source.containerOptionId),
    containerTypeId: toNumber(source.containerTypeId),
    fuelTypeId: toNumber(source.fuelTypeId),
    maxVolume: clampRange(
      toNumber(source.maxVolume) ?? MAX_VOLUME_KG,
      MIN_VOLUME_KG,
      MAX_VOLUME_KG
    ),
    maxPricePerKg: clampRange(
      toNumber(source.maxPricePerKg) ?? MAX_PRICE_PER_KG,
      MIN_PRICE_PER_KG,
      MAX_PRICE_PER_KG
    ),
    radiusKm: clampRadiusKm(source.radiusKm),
  };
};

export const filtersEqual = (left, right) => {
  const a = normaliseFilters(left);
  const b = normaliseFilters(right);

  return FILTER_KEYS.every((key) => a[key] === b[key]);
};

export const isFilterActive = (filters, key) => {
  const value = normaliseFilters(filters)[key];

  return value !== INITIAL_FILTERS[key];
};

export const countActiveFilters = (filters) =>
  FILTER_KEYS.filter((key) => isFilterActive(filters, key)).length;

export const hasProductFilters = (filters) =>
  PRODUCT_FILTER_KEYS.some((key) => isFilterActive(filters, key));

export const clearProducts = (filters) => ({
  ...normaliseFilters(filters),
  containerOptionId: INITIAL_FILTERS.containerOptionId,
  containerTypeId: INITIAL_FILTERS.containerTypeId,
  fuelTypeId: INITIAL_FILTERS.fuelTypeId,
  maxVolume: INITIAL_FILTERS.maxVolume,
  maxPricePerKg: INITIAL_FILTERS.maxPricePerKg,
});

export const buildSearchParams = ({ coordinates, filters, size, distanceMeters }) => {
  const applied = normaliseFilters(filters);
  const cell = quantiseCoordinates(coordinates);
  const distance = distanceMeters ?? radiusToMeters(applied.radiusKm);

  const params = {
    sort: FEED_SORT,
    size: size ?? FEED_PAGE_SIZE,
  };

  if (cell && distance > 0) {
    params["location.latitude"] = cell.latitude;
    params["location.longitude"] = cell.longitude;
    params["location.distanceMeters"] = distance;
  }

  if (applied.containerOptionId !== null) {
    params["products.containerOptionId"] = applied.containerOptionId;
  }

  if (applied.containerTypeId !== null) {
    params["products.containerTypeId"] = applied.containerTypeId;
  }

  if (applied.fuelTypeId !== null) {
    params["products.fuelTypeId"] = applied.fuelTypeId;
  }

  if (applied.maxVolume !== INITIAL_FILTERS.maxVolume) {
    params["products.maxVolume"] = applied.maxVolume;
  }

  if (applied.maxPricePerKg !== INITIAL_FILTERS.maxPricePerKg) {
    params.maxPricePerKg = applied.maxPricePerKg;
  }

  return params;
};

export const searchSignature = (params) =>
  Object.keys(params || {})
    .sort()
    .map((key) => `${key}=${params[key]}`)
    .join("&");
